import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ["All", "Buying", "Selling", "General"];

  const filteredFaqs = filter === "All" ? faqs : faqs.filter(faq => faq.category === filter);

  return (
    <div data-testid="faq-page">
      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">Frequently Asked Questions</h1>
          <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto leading-relaxed">
            Find answers to common questions about buying and selling in the GTA
          </p>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap gap-4 justify-center mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setFilter(category)}
                variant={filter === category ? "default" : "outline"}
                className={`h-12 px-6 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300 ${
                  filter === category
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border-2 border-primary text-primary hover:bg-primary hover:text-white'
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20" data-testid="loading-indicator">
              <p className="text-muted-foreground">Loading FAQs...</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full space-y-4" data-testid="faq-accordion">
              {filteredFaqs.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id} className="bg-white border border-border" data-testid={`faq-item-${faq.id}`}>
                  <AccordionTrigger className="px-8 py-6 text-left hover:no-underline hover:bg-secondary/30 transition-colors">
                    <div className="flex items-start w-full">
                      <div className="flex-1">
                        <p className="text-lg font-heading font-semibold text-primary mb-2">{faq.question}</p>
                        <span className="text-xs uppercase tracking-widest text-accent font-semibold">{faq.category}</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-8 pb-6 text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {!loading && filteredFaqs.length === 0 && (
            <div className="text-center py-20" data-testid="no-results">
              <p className="text-muted-foreground">No FAQs found in this category.</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 md:py-32 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-primary mb-6">
            Still Have Questions?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            I'm here to help! Feel free to reach out with any questions about the GTA real estate market or your specific situation.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-10 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
              data-testid="contact-button"
            >
              Get In Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;